import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { IntegrationType } from '@kcftech/prometheus-client';
import { keepPreviousData } from '@tanstack/react-query';
import { Detail } from 'components/activity/Detail';
import { IconButton } from 'components/button/IconButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { CollapsiblePane } from 'components/expandable_pane/CollapsiblePane';
import { DetailsPage } from 'components/page/DetailsPage';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { usePagedUsersDataSource } from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { useActiveIntegration } from 'custom_hooks/data_hooks/integrations/useActiveIntegration';
import { useLocation } from 'custom_hooks/data_hooks/location/useLocation';
import { useLocationKcfInfo } from 'custom_hooks/data_hooks/location/useLocationKcfInfo';
import { useMaxUpdateIntervalOptions } from 'custom_hooks/data_hooks/location/useMaxUpdateIntervalOptions';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { AutoMaintenancePopup } from 'pages/Desk/issues/popup/AutoMaintenancePopup';
import { LocationNameInlineEditor } from 'pages/Settings/Locations/LocationNameInlineEditor';
import { LocationNetSuiteIdInlineEditor } from 'pages/Settings/Locations/LocationNetSuiteIdInlineEditor';
import { LocationAbbreviationCell } from 'pages/Settings/Locations/table_cells/LocationAbbreviationCell';
import { LocationActiveCell } from 'pages/Settings/Locations/table_cells/LocationActiveCell';
import { LocationApiKeyCell } from 'pages/Settings/Locations/table_cells/LocationApiKeyCell';
import { LocationAutoMaintenanceCell } from 'pages/Settings/Locations/table_cells/LocationAutoMaintenanceCell';
import { LocationCorporationCell } from 'pages/Settings/Locations/table_cells/LocationCorporationCell';
import { LocationDraftIssuesAvailableCell } from 'pages/Settings/Locations/table_cells/LocationDraftIssuesAvailableCell';
import { LocationDynamicSystemsAllowedForDeskCell } from 'pages/Settings/Locations/table_cells/LocationDynamicSystemsAllowedForDeskCell';
import { LocationFastestOilQualityUpdateCell } from 'pages/Settings/Locations/table_cells/LocationFastestOilQualityUpdateCell';
import { LocationFastestVibrationUpdateCell } from 'pages/Settings/Locations/table_cells/LocationFastestVibrationUpdateCell';
import { LocationLineFrequencyInlineEditor } from 'pages/Settings/Locations/table_cells/LocationLineFrequencyCell';
import { LocationMaxTransportUpdateCell } from 'pages/Settings/Locations/table_cells/LocationMaxTransportUpdateCell';
import { LocationModelPublicationCell } from 'pages/Settings/Locations/table_cells/LocationModelPublicationCell';
import { LocationOPCEnabledCell } from 'pages/Settings/Locations/table_cells/LocationOPCEnabledCell';
import { LocationSubscriptionServiceCell } from 'pages/Settings/Locations/table_cells/LocationSubscriptionServiceCell';
import { LocationValueUnitCell } from 'pages/Settings/Locations/table_cells/LocationValueUnitCell';
import { SettingsHeading } from 'pages/Settings/shared/styled';
import { CreateUsersModal } from 'pages/Settings/Users/CreateUsersModal';
import { UsersTable } from 'pages/Settings/Users/UsersTable';
import { useEffect, useState } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled from 'styled-components';
import { LocationDeskChatUserGroupCell } from './table_cells/LocationDeskChatUserGroupCell';

const LocationContainer = styled(SettingsHeading)`
    display: flex;
    flex-direction: row;
`;

export type SingleLocationPageViewProps = {
    locationId: string;
};

export function SingleLocationPage({ locationId }: SingleLocationPageViewProps) {
    const user = useUser();
    const { canViewKcfInfo } = usePermissions();

    const [usersPaneOpen, setUsersPaneOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);

    const locationIds = useValueAsTuple(locationId);
    const {
        data: location,
        refetch,
        isLoading,
        isFetching,
    } = useLocation(locationId, {
        placeholderData: keepPreviousData,
    });

    const reload = () => {
        refetch();
        user.updateFavorites();
    };

    const { data: options, isLoading: optionsLoading } = useMaxUpdateIntervalOptions();
    const { data: activeIntegration } = useActiveIntegration(
        locationId,
        IntegrationType.Cmms,
    );

    const locationUsersDataSource = usePagedUsersDataSource({
        name: 'location-users',
        defaultFilter: {
            locationIds,
            nameSearch: '',
        },
    });

    const { setFilter } = locationUsersDataSource;

    const onUserCreate = () => {
        setAddModalOpen(false);
        locationUsersDataSource.reloadPage();
    };

    useEffect(() => {
        setFilter((current) => ({
            ...current,
            locationIds,
        }));
    }, [locationIds, setFilter]);

    return (
        <DetailsPage
            isLoading={isLoading || isFetching}
            fullHeight
            fullWidth
            noPageScroll
        >
            {location && <DocumentTitle title={`${location.Name} Location Settings`} />}
            {location && (
                <div className="p-35" style={{ overflowY: 'auto' }}>
                    <LocationContainer>
                        <LocationNameInlineEditor
                            location={location}
                            onChange={refetch}
                        />
                    </LocationContainer>
                    <div className="py-20">
                        <Detail label="Abbreviation" labelClassName="text-nowrap">
                            <LocationAbbreviationCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="Corporation" labelClassName="text-nowrap">
                            <LocationCorporationCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="Created">
                            <DateRelativeToNow date={location.Created} />
                        </Detail>
                        <Detail label="Active">
                            <LocationActiveCell rowData={location} onChange={reload} />
                        </Detail>

                        <Detail
                            label={
                                <div className="flex-row align-center">
                                    Automatic Maintenance Task Creation
                                    <AutoMaintenancePopup
                                        softwareName={activeIntegration?.SoftwareName}
                                    />
                                </div>
                            }
                        >
                            <LocationAutoMaintenanceCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail
                            label={
                                <div className="flex-row align-center">
                                    Show Dynamic System Associations for Issues
                                    <InfoPopup title="Show Dynamic System Associations for Issues">
                                        Enabling this option will visibly tie machine
                                        health issues to dynamic systems from the
                                        workbench, and will show this field throughout
                                        machine health issues in desk. Dynamic systems are
                                        used to display only the groups whose hardware is
                                        connected to certain base station(s). If
                                        you&apos;re unsure whether you need to enable this
                                        field, please reach out to a system administrator.
                                    </InfoPopup>
                                </div>
                            }
                        >
                            <LocationDynamicSystemsAllowedForDeskCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="Draft Issues Available to Customer Admins">
                            <LocationDraftIssuesAvailableCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail
                            label="Fastest Update Interval (Vibration)"
                            labelClassName="text-nowrap"
                        >
                            <SpinnerArea isLoading={optionsLoading}>
                                {options?.VibrationUpdateIntervals && (
                                    <LocationFastestVibrationUpdateCell
                                        rowData={location}
                                        onChange={refetch}
                                        options={options.VibrationUpdateIntervals}
                                    />
                                )}
                            </SpinnerArea>
                        </Detail>
                        <Detail
                            label="Fastest Update Interval (Oil Quality)"
                            labelClassName="text-nowrap"
                        >
                            <SpinnerArea isLoading={optionsLoading}>
                                {options?.OilQualityUpdateIntervals && (
                                    <LocationFastestOilQualityUpdateCell
                                        rowData={location}
                                        onChange={refetch}
                                        options={options.OilQualityUpdateIntervals}
                                    />
                                )}
                            </SpinnerArea>
                        </Detail>
                        <Detail
                            label="Max Transport Update (seconds)"
                            labelClassName="text-nowrap"
                        >
                            <LocationMaxTransportUpdateCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail
                            label="Electrical Line Frequency"
                            labelClassName="text-nowrap"
                        >
                            <LocationLineFrequencyInlineEditor
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="API Key" labelClassName="text-nowrap">
                            <LocationApiKeyCell rowData={location} onChange={refetch} />
                        </Detail>
                        <Detail label="Enable OPC" labelClassName="text-nowrap">
                            <LocationOPCEnabledCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="Service Subscription" labelClassName="text-nowrap">
                            <LocationSubscriptionServiceCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail label="Model Publication" labelClassName="text-nowrap">
                            <LocationModelPublicationCell
                                rowData={location}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail
                            label="Value Unit"
                            labelClassName="text-nowrap"
                            infoText={
                                <>
                                    The primary units of output for this location. Used
                                    when presenting some &quot;value delivered&quot;
                                    statistics.
                                </>
                            }
                        >
                            <LocationValueUnitCell
                                locationId={location.Id}
                                data={location.ValueUnit}
                                onChange={refetch}
                            />
                        </Detail>
                        <Detail
                            label="Desk Chat Auto Reply"
                            labelClassName="text-nowrap"
                            infoText={
                                <>
                                    Whose Issue comments Desk Chat will automatically
                                    reply to
                                </>
                            }
                        >
                            <LocationDeskChatUserGroupCell
                                locationId={location.Id}
                                data={location.LocationPermissions}
                                onChange={refetch}
                            />
                        </Detail>
                        {canViewKcfInfo && <KcfInfoSection locationId={location.Id} />}
                        <CollapsiblePane
                            title="Users"
                            expanded={usersPaneOpen}
                            onChange={setUsersPaneOpen}
                        >
                            <div className="p-20">
                                <div className="flex-row justify-flex-end mb-5">
                                    <IconButton
                                        icon={faPlus}
                                        onClick={() => setAddModalOpen(true)}
                                    >
                                        Add User
                                    </IconButton>
                                </div>
                                <UsersTable
                                    dataSource={locationUsersDataSource}
                                    locationId={location.Id}
                                />
                            </div>
                        </CollapsiblePane>
                    </div>
                </div>
            )}
            {addModalOpen && (
                <CreateUsersModal
                    location={location}
                    onCreate={onUserCreate}
                    onClose={() => setAddModalOpen(false)}
                />
            )}
        </DetailsPage>
    );
}

const KcfInfoSection = ({
    locationId,
    onChange,
}: {
    locationId: string;
    onChange?: () => void;
}) => {
    const { data: kcfInformation, isLoading, refetch } = useLocationKcfInfo(locationId);

    return (
        <SpinnerArea noContainer isLoading={isLoading}>
            {kcfInformation && (
                <Detail label="NetSuite ID">
                    <LocationNetSuiteIdInlineEditor
                        locationId={locationId}
                        locationKcfInfo={kcfInformation}
                        onChange={() => {
                            refetch();
                            onChange?.();
                        }}
                    />
                </Detail>
            )}
        </SpinnerArea>
    );
};
